import { Marker } from "react-map-gl/maplibre"
import { Box, Text } from "@chakra-ui/react"

const severityColors = {
  critical: "#dc2626", // red
  warning: "#d97706",  // amber
  info: "#3b82f6",     // blue
  healthy: "#059669",  // green
}

const assetKindIcons = {
  tube_trailer: "🚛",
  manifold_cylinder_pallet: "📦",
  generator: "⚙️",
  remote_monitoring: "📡",
  gas_management: "🔧",
  hose: "🔗",
  tow_tractor: "🚜",
  dual_fuel_vehicle: "🚗",
  battery: "🔋",
  dynamic_storage: "🏭",
}

interface Asset {
  id: string
  name: string
  kind: string
  status: string
  alerts?: {
    total: number
    highestSeverity?: string | null
  } | null
}

interface AssetMarkerProps {
  asset: Asset
  latitude: number
  longitude: number
  onClick: () => void
  isSelected: boolean
  isSpiderfied?: boolean
}

function AssetMarker({ asset, latitude, longitude, onClick, isSelected, isSpiderfied = false }: AssetMarkerProps) {
  const hasAlerts = asset.alerts && asset.alerts.total > 0
  const severity = hasAlerts ? asset.alerts?.highestSeverity || "info" : "healthy"
  const color = severityColors[severity as keyof typeof severityColors] || severityColors.healthy
  const icon = ""

  return (
    <Marker
      latitude={latitude}
      longitude={longitude}
      onClick={(e) => {
        e.originalEvent.stopPropagation()
        onClick()
      }}
    >
      <Box
        position="relative"
        cursor="pointer"
        transform={isSelected ? "scale(1.2)" : "scale(1)"}
        transition="transform 0.2s ease"
        _hover={{ transform: "scale(1.1)" }}
      >
        {/* Pulse ring for alerts */}
        {hasAlerts && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="60px"
            height="60px"
            borderRadius="50%"
            border={`2px solid ${color}`}
            opacity={0.4}
            animation="ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite"
          />
        )}

        {/* Main marker */}
        <Box
          width={isSpiderfied ? "36px" : "44px"}
          height={isSpiderfied ? "36px" : "44px"}
          borderRadius="50%"
          bg={color}
          border={`3px solid ${isSelected ? "white" : isSpiderfied ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)"}`}
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow={isSpiderfied ? `0 4px 12px ${color}66` : `0 4px 20px ${color}66, 0 0 0 ${isSelected ? "4px" : "0"} rgba(255,255,255,0.1)`}
          position="relative"
        >
          <Text fontSize={isSpiderfied ? "14px" : "18px"} role="img" aria-label={asset.kind}>
            {icon}
          </Text>

          {/* Alert badge */}
          {hasAlerts && (
            <Box
              position="absolute"
              top="-4px"
              right="-4px"
              width="20px"
              height="20px"
              borderRadius="50%"
              bg="#dc2626"
              border="2px solid white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="10px"
              fontWeight="700"
              color="white"
            >
              {asset.alerts!.total}
            </Box>
          )}
        </Box>

        {/* Asset label */}
        <Box
          position="absolute"
          top="100%"
          left="50%"
          transform="translateX(-50%)"
          mt={2}
          whiteSpace="nowrap"
          fontSize="11px"
          fontWeight="500"
          bg="rgba(0,0,0,0.8)"
          color="white"
          px={2}
          py={1}
          borderRadius="4px"
          opacity={isSelected ? 1 : 0.8}
          maxWidth="120px"
          truncate
        >
          {asset.name}
        </Box>
      </Box>

      {/* CSS for ping animation */}
      <style>
        {`
          @keyframes ping {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
            75%, 100% { transform: translate(-50%, -50%) scale(1.8); opacity: 0; }
          }
        `}
      </style>
    </Marker>
  )
}

export default AssetMarker